/**
 * AuthContext - Real JWT auth (TD-14 restoration, Checkpoint 4).
 *
 * State machine:
 *   user = null   → bootstrapping (calling /auth/me on mount)
 *   user = false  → not authenticated (token missing or invalid)
 *   user = object → authenticated
 *
 * Token is stored in localStorage under `pgf_token`. Backend supports
 * Authorization: Bearer header; httpOnly cookies are deferred to a future
 * security pass (out of scope for Phase 1).
 *
 * A global axios interceptor:
 *   1. Attaches Bearer token to every request when present
 *   2. Clears token + redirects to /login on 401 responses
 */
import { createContext, useContext, useState, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import ttsPlayer from "../services/ttsPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pgf_token";

const AuthContext = createContext(null);

// ── Error helper for FastAPI 422 array-shaped detail ────────────────────
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ── Global axios interceptors (set once) ────────────────────────────────
let interceptorsInstalled = false;
function installInterceptors(onUnauthorized) {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.request.use((config) => {
    // Only attach to PGF API calls
    const url = config?.url || "";
    if (url.includes("/api/")) {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token && !config.headers?.Authorization) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      // Only handle PGF API 401s. Skip the /auth/me bootstrap call so it can
      // resolve quietly to "logged out" on first load.
      if (status === 401 && url.includes("/api/") && !url.includes("/auth/me")) {
        onUnauthorized?.();
      }
      return Promise.reject(error);
    }
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const onUnauthorizedRef = useRef(null);

  // Set up interceptors once
  useEffect(() => {
    onUnauthorizedRef.current = () => {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
      // Avoid hard redirect during /auth/me bootstrap or login/signup pages
      const path = window.location.pathname;
      const publicPaths = ["/", "/login", "/signup", "/demo", "/forgot-password", "/reset-password"];
      const isPublic = publicPaths.includes(path) || path.startsWith("/join/");
      if (!isPublic) {
        // Use replace to avoid back-button loop into a 401'd page
        window.location.replace("/login");
      }
    };
    installInterceptors(() => onUnauthorizedRef.current?.());
  }, []);

  // Bootstrap: call /auth/me if token exists
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  // [P0 invite-gated access] `inviteCode` is what unlocks registration while
  // the deploy is admin_test - the backend validates it against a real league
  // and 403s without it.
  const register = async (first_name, last_name, email, password, inviteCode = null) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      first_name,
      last_name,
      email,
      password,
      invite_code: inviteCode || undefined,
    });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    // [Phase 2c-B §4.12 NEW-3] stop any in-flight Adam audio and clear cache.
    try { ttsPlayer.reset(); } catch (_e) { /* noop */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
    window.location.replace("/login");
  };

  const isLoading = user === null;
  const isAuthenticated = !!user && user !== false;

  return (
    <AuthContext.Provider
      value={{ user: isAuthenticated ? user : null, login, register, logout, isLoading, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// ── ProtectedRoute - gate by real auth ──────────────────────────────────
export function ProtectedRoute({ children }) {
  const { isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#111111]"
        data-testid="auth-loading"
      >
        <div className="font-display font-black text-2xl uppercase tracking-widest text-[#FF6B00] animate-pulse">
          Loading…
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return children;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
